import { type ApolloError, useMutation } from '@apollo/client';
import isEqual from 'lodash/isEqual';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { useAuth } from '../../../../components/AuthContext/context.ts';
import { ActivateAudienceDocument } from '../../../../graphql/mutation/audiences/ActivateAudience.generated.tsx';
import {
  CreateAudienceSharesDocument,
  type CreateAudienceSharesMutation,
  type CreateAudienceSharesMutationVariables,
} from '../../../../graphql/mutation/audiences/CreateAudienceShares.generated.tsx';
import {
  DeleteAudienceSharesDocument,
  type DeleteAudienceSharesMutation,
} from '../../../../graphql/mutation/audiences/DeleteAudienceShares.generated.tsx';
import {
  UpdateAudienceDocument,
  type UpdateAudienceMutationVariables,
} from '../../../../graphql/mutation/audiences/UpdateAudience.generated.tsx';
import {
  UpdateAudienceSharesDocument,
  type UpdateAudienceSharesMutation,
} from '../../../../graphql/mutation/audiences/UpdateAudienceShares.generated.tsx';
import { CreateLogDocument } from '../../../../graphql/mutation/error/createLog.generated.tsx';
import type { GetAudienceQuery } from '../../../../graphql/query/audiences/GetAudience.generated.tsx';
import { NestedErrorCode } from '../../../graphql/types.ts';
import { useLocalErrorHandling } from '../../../graphql/useLocalErrorHandling.ts';
import { mapAudienceShareToForm } from '../../utils/mapAudienceShareToForm.ts';
import type { AudienceForm } from '../types.ts';
import { filterNormalizedEmptyGroups } from '../utils/filterNormalizedEmptyGroups.ts';
import { getSubmittedExportTargets } from '../utils/getSelectedExportTargets.ts';
import { isAudienceNameConflictError, isAudienceNamePath } from '../utils/isAudienceNameConflictError.ts';
import { isSegmentErrorMessage, parseSegmentErrorMessages } from './utils/segmentErrorMessages.ts';
import { prepareSharesPayloads } from './utils/sharesPayload.ts';

const PERCENT_TO_DECIMAL_FACTOR = 100;

export type AudienceShare = ReturnType<typeof mapAudienceToCreateShareMutation>[number];

export const mapAudienceToCreateShareMutation = (shares: AudienceForm['audience_share']) => {
  const result: CreateAudienceSharesMutationVariables['input'] = [];

  shares.forEach(share => {
    share.recipient_account_id.forEach(accountId => {
      if (share.revenue_method === 'PoM') {
        result.push({
          audience_external_openaudience_id: share.audience_external_openaudience_id,
          recipient_account_id: accountId,
          revenue_method: 'PoM',
          third_party_revenue: share.isFeeEnabled
            ? (parseFloat(share.value) / PERCENT_TO_DECIMAL_FACTOR).toString()
            : '0',
        });
      } else {
        result.push({
          audience_external_openaudience_id: share.audience_external_openaudience_id,
          recipient_account_id: accountId,
          revenue_method: 'CPM',
          cpm_cap: share.isFeeEnabled ? share.value : '0',
        });
      }
    });
  });

  return result;
};

export const mapAudienceToUpdateMutation = (
  formValues: AudienceForm,
  filteredAudienceCriteria: AudienceForm['normalized_definition']
): UpdateAudienceMutationVariables['data'] => {
  return {
    name: formValues.name,
    description: formValues.description,
    export_targets: getSubmittedExportTargets(formValues),
    export_type: formValues.export_type,
    direct_audience_provider: formValues.direct_audience_provider,
    normalized_definition: filteredAudienceCriteria,
  };
};

export const useUpdateAndActivate = (audienceUid: string) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [createAudienceShares] = useMutation(CreateAudienceSharesDocument, {
    refetchQueries: ['GetAudienceShares', 'GetAudience'],
  });

  const [updateAudienceShares] = useMutation(UpdateAudienceSharesDocument, {
    refetchQueries: ['GetAudienceShares', 'GetAudience'],
  });

  const [deleteAudienceShares] = useMutation(DeleteAudienceSharesDocument, {
    refetchQueries: ['GetAudienceShares', 'GetAudience'],
  });

  const [updateAudience] = useMutation(UpdateAudienceDocument, {
    refetchQueries: ['GetAudiencesList', 'GetAudiencesList'],
  });

  const [activateAudience] = useMutation(ActivateAudienceDocument, {
    refetchQueries: ['GetAudiencesList'],
  });

  const [pushLog] = useMutation(CreateLogDocument);

  useLocalErrorHandling(
    UpdateAudienceDocument,
    useCallback(
      error => {
        if (error.code === NestedErrorCode.BAD_USER_INPUT) {
          enqueueSnackbar(parseSegmentErrorMessages(error.message, true, t), { variant: 'error' });
          return true;
        }

        if (error.code === NestedErrorCode.JOINT_UNIQUENESS_VIOLATION) {
          if (!isAudienceNamePath(error.path)) return false;
          enqueueSnackbar(t('Audience with such name already exists'), { variant: 'error' });
          return true;
        }

        return false;
      },
      [enqueueSnackbar, t]
    )
  );

  const goBack = useCallback(() => {
    if (location.state?.ref) {
      navigate(-1);
    } else {
      navigate('/audiences');
    }
  }, [location.state, navigate]);

  const updateAndActivateAudience = useCallback(
    async (formValues: AudienceForm, initialAudience: NonNullable<GetAudienceQuery['audience_by_pk']>) => {
      const filteredAudienceCriteria = filterNormalizedEmptyGroups(formValues.normalized_definition);

      if (filteredAudienceCriteria.includes.groups.length === 0) {
        enqueueSnackbar(t('At least one segment should be selected'), { variant: 'error' });

        return;
      }

      const updatedShares = mapAudienceToCreateShareMutation(formValues.audience_share);
      const initialShares = mapAudienceToCreateShareMutation(
        mapAudienceShareToForm(initialAudience.id, initialAudience.audience_share)
      );

      try {
        if (!isEqual(updatedShares, initialShares)) {
          const initialSharesRecipientToShareUid = new Map<string, string>();

          initialAudience.audience_share.forEach(share => {
            if (share?.recipient_account_id && share?.uid) {
              initialSharesRecipientToShareUid.set(share.recipient_account_id, share.uid);
            }
          });

          const { audienceShareCreatePayload, audienceShareUpdatePayload, audienceShareDeletePayload } =
            prepareSharesPayloads(initialShares, updatedShares, initialSharesRecipientToShareUid);

          try {
            let audienceShareDelete: DeleteAudienceSharesMutation['audienceShareDelete'] = [];

            if (audienceShareDeletePayload.length) {
              const result = await deleteAudienceShares({ variables: { input: audienceShareDeletePayload } });

              audienceShareDelete = result.data?.audienceShareDelete ?? [];
            }

            let audienceShareUpdate: UpdateAudienceSharesMutation['audienceShareUpdate'] = [];

            if (audienceShareUpdatePayload.length) {
              const updateResp = await updateAudienceShares({
                variables: { input: audienceShareUpdatePayload },
              });

              audienceShareUpdate = updateResp.data?.audienceShareUpdate ?? [];
            }

            let audienceShareCreate: CreateAudienceSharesMutation['audienceShareCreate'] = [];

            if (audienceShareCreatePayload.length) {
              const createResp = await createAudienceShares({
                variables: { input: audienceShareCreatePayload },
              });

              audienceShareCreate = createResp.data?.audienceShareCreate ?? [];
            }

            const deleteErrors = audienceShareDelete.filter(response => response && response.__typename === 'Error');
            const updateErrors = audienceShareUpdate.filter(response => response && response.__typename === 'Error');
            const createErrors = audienceShareCreate.filter(response => response && response.__typename === 'Error');

            const results = [...audienceShareDelete, ...audienceShareUpdate, ...audienceShareCreate];
            const errors = [...deleteErrors, ...updateErrors, ...createErrors];

            const getWarningMessage = ({
              audienceShareDelete,
              deleteErrors,
              audienceShareUpdate,
              updateErrors,
              audienceShareCreate,
              createErrors,
            }) => {
              const deleteShareCount = audienceShareDelete.length;
              const updateShareCount = audienceShareUpdate.length;
              const createShareCount = audienceShareCreate.length;

              const deleteErrorCount = deleteErrors.length;
              const updateErrorCount = updateErrors.length;
              const createErrorCount = createErrors.length;

              const successfulDeletes = deleteShareCount - deleteErrorCount;
              const successfulUpdates = updateShareCount - updateErrorCount;
              const successfulCreates = createShareCount - createErrorCount;

              const deleteCaption =
                deleteShareCount > 0 ? `${t('Deleted')}: ${successfulDeletes}/${deleteShareCount}.` : '';
              const updateCaption =
                updateShareCount > 0 ? `${t('Updated')}: ${successfulUpdates}/${updateShareCount}.` : '';
              const createCaption =
                createShareCount > 0 ? `${t('Created')}: ${successfulCreates}/${createShareCount}.` : '';

              return `${t(
                'Audience was shared partially.'
              )} ${deleteCaption} ${updateCaption} ${createCaption}`.replace(/ {2,}/g, ' ');
            };

            const warningMessage = getWarningMessage({
              audienceShareDelete,
              deleteErrors,
              audienceShareUpdate,
              updateErrors,
              audienceShareCreate,
              createErrors,
            });

            if (errors.length === 0) {
              enqueueSnackbar(t('Audience shared successfully'), { variant: 'success' });
            } else {
              if (errors.length < results.length) {
                enqueueSnackbar(warningMessage, { variant: 'warning' });
              } else {
                enqueueSnackbar(t('Cannot share audience'), { variant: 'error' });
              }
            }
          } catch (_) {
            await pushLog({
              variables: {
                location: window.location.href,
                msg: 'Failed to share audiences',
                name: currentUser?.displayName,
                email: currentUser?.email,
              },
            });

            enqueueSnackbar(t('Failed to share the audience'), { variant: 'error' });
          }
        }

        await updateAudience({
          variables: {
            data: mapAudienceToUpdateMutation(formValues, filteredAudienceCriteria),
            id: audienceUid,
          },
        });

        await activateAudience({ variables: { id: audienceUid } });

        goBack();
        enqueueSnackbar(t('Audience updated successfully.'), { variant: 'success' });
      } catch (error) {
        const apolloError = error as ApolloError;
        const { message } = apolloError;
        const isJointUniquenessViolation = isAudienceNameConflictError(apolloError);

        if (
          !isJointUniquenessViolation &&
          message !== 'Validation error' &&
          message !== 'Joint uniqueness violation' &&
          !isSegmentErrorMessage(message)
        ) {
          enqueueSnackbar(message, { variant: 'error' });
        }

        await pushLog({
          variables: {
            location: window.location.href,
            msg: message,
            name: currentUser?.displayName,
            email: currentUser?.email,
          },
        });

        if (isJointUniquenessViolation) {
          throw error;
        }
      }
    },
    [
      updateAudience,
      activateAudience,
      enqueueSnackbar,
      audienceUid,
      createAudienceShares,
      currentUser?.displayName,
      currentUser?.email,
      deleteAudienceShares,
      goBack,
      pushLog,
      t,
      updateAudienceShares,
    ]
  );

  return updateAndActivateAudience;
};
